import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import ServiceAgreementGate from "../components/ServiceAgreementGate";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Clock, Tag, CreditCard, ArrowRight, Shield } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export default function ServiceMarketplace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [hours, setHours] = useState("1");
  const [processing, setProcessing] = useState(false);
  const [processor, setProcessor] = useState("fiserv");
  const [showAgreementGate, setShowAgreementGate] = useState(false);
  const [agreementId, setAgreementId] = useState(null);
  const [hasSignedAgreement, setHasSignedAgreement] = useState(null);
  const fiservFormRef = React.useRef(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await apiClient.get("/services?active_only=true");
        setServices(res.data);
      } catch { toast.error("Failed to load services"); }
      finally { setLoading(false); }
    };
    fetchServices();
    // Check if client already has a signed agreement
    const checkAgreement = async () => {
      try {
        const res = await apiClient.get(`/agreements/check/${user.id}`);
        setHasSignedAgreement(res.data.has_agreement);
        if (res.data.has_agreement) setAgreementId(res.data.agreement_id);
      } catch {
        setHasSignedAgreement(false);
      }
    };
    checkAgreement();
  }, [user.id]);

  const calculateTotal = () => {
    if (!selectedService) return 0;
    if (selectedService.pricing_type === "hourly") {
      return selectedService.price * parseFloat(hours || 1);
    }
    return selectedService.price;
  };

  const handleCheckout = async () => {
    if (!selectedService) return;

    // Enforce agreement gate — must sign before payment
    if (!hasSignedAgreement && !agreementId) {
      setShowAgreementGate(true);
      return;
    }

    setProcessing(true);
    try {
      const res = await apiClient.post("/payments/checkout", {
        service_id: selectedService.id,
        hours: selectedService.pricing_type === "hourly" ? parseFloat(hours) : 1,
        origin_url: window.location.origin,
        processor,
        agreement_id: agreementId || null,
      });

      if (processor === "fiserv" && res.data.gateway_url && res.data.form_params) {
        // Fiserv IPG Connect — auto-submit form to hosted payment page
        const form = fiservFormRef.current;
        if (form) {
          form.action = res.data.gateway_url;
          // Clear existing hidden inputs
          while (form.firstChild) form.removeChild(form.firstChild);
          Object.entries(res.data.form_params).forEach(([key, value]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });
          form.submit();
        }
      } else if (res.data.url) {
        // Stripe redirect
        window.location.href = res.data.url;
      } else {
        toast.error(res.data.message || "Payment not available");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="service-marketplace-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">{t("services.title")}</h1>
          <p className="text-slate-500 mt-1">{t("services.subtitle")}</p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading services...</div>
        ) : services.length === 0 ? (
          <Card className="sag-card">
            <CardContent className="p-8 text-center">
              <ShoppingCart className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">{t("services.noServices")}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Card key={service.id} className="sag-card hover:shadow-lg transition-shadow cursor-pointer group" onClick={() => setSelectedService(service)} data-testid={`service-card-${service.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="font-heading text-lg">{service.name}</CardTitle>
                      {service.product_name && (
                        <Badge variant="secondary" className="mt-1 bg-slate-100 text-slate-600 text-xs">{service.product_name}</Badge>
                      )}
                    </div>
                    <Badge variant="secondary" className={service.pricing_type === "hourly" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}>
                      {service.pricing_type === "hourly" ? <Clock className="h-3 w-3 mr-1" /> : <Tag className="h-3 w-3 mr-1" />}
                      {service.pricing_type === "hourly" ? "Hourly" : "Fixed"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{service.description}</p>
                  <div className="flex items-end justify-between">
                    <div>
                      <span className="text-3xl font-bold text-sag-navy">${service.price.toFixed(2)}</span>
                      {service.pricing_type === "hourly" && <span className="text-slate-500 text-sm">/hour</span>}
                    </div>
                    <Button size="sm" className="bg-sag-navy hover:bg-sag-navy-800 group-hover:translate-x-1 transition-transform">
                      Get Started <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Agreement Gate Dialog */}
        <Dialog open={showAgreementGate} onOpenChange={(open) => { if (!open) setShowAgreementGate(false); }}>
          <DialogContent className="sm:max-w-[750px] max-h-[90vh] overflow-y-auto p-0">
            <DialogHeader className="sr-only"><DialogTitle>Service Agreement</DialogTitle><DialogDescription>Review and sign the service agreement</DialogDescription></DialogHeader>
            <ServiceAgreementGate
              serviceId={selectedService?.id}
              onAgreementSigned={(id) => {
                setAgreementId(id);
                setHasSignedAgreement(true);
                setShowAgreementGate(false);
                toast.success("Agreement signed! You may now proceed to payment.");
              }}
              onCancel={() => setShowAgreementGate(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Checkout Dialog */}
        <Dialog open={!!selectedService && !showAgreementGate} onOpenChange={(open) => { if (!open) setSelectedService(null); }}>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Confirm & Pay</DialogTitle>
            </DialogHeader>
            {selectedService && (
              <div className="space-y-5 mt-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <h3 className="font-semibold text-slate-900">{selectedService.name}</h3>
                  <p className="text-sm text-slate-600 mt-1">{selectedService.description}</p>
                </div>

                {selectedService.pricing_type === "hourly" && (
                  <div className="space-y-2">
                    <Label>How many hours do you need?</Label>
                    <Input type="number" step="0.5" min="0.5" value={hours}
                      onChange={(e) => setHours(e.target.value)} data-testid="checkout-hours-input" />
                    <p className="text-xs text-slate-500">Rate: ${selectedService.price.toFixed(2)}/hour</p>
                  </div>
                )}

                <div className="bg-sag-navy/5 rounded-xl p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700 font-medium">Total Due</span>
                    <span className="text-2xl font-bold text-sag-navy" data-testid="checkout-total">
                      ${calculateTotal().toFixed(2)} <span className="text-xs font-normal text-slate-500">BSD</span>
                    </span>
                  </div>
                  {selectedService.pricing_type === "hourly" && (
                    <p className="text-xs text-slate-500 mt-1">{hours} hour(s) x ${selectedService.price.toFixed(2)}</p>
                  )}
                </div>

                {/* Payment Processor Selection */}
                <div className="space-y-2">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Payment Method</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setProcessor("fiserv")}
                      className={`p-3 rounded-lg border-2 text-center transition-colors ${
                        processor === "fiserv"
                          ? "border-sag-navy bg-sag-navy/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="processor-fiserv"
                    >
                      <p className="font-semibold text-sm text-slate-900">Scotiabank</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Primary (BSD)</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setProcessor("stripe")}
                      className={`p-3 rounded-lg border-2 text-center transition-colors ${
                        processor === "stripe"
                          ? "border-sag-navy bg-sag-navy/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid="processor-stripe"
                    >
                      <p className="font-semibold text-sm text-slate-900">Stripe</p>
                      <p className="text-[10px] text-slate-500 mt-0.5">Backup (USD)</p>
                    </button>
                  </div>
                </div>

                <div className="flex items-start gap-2 text-xs text-slate-500">
                  <Shield className="h-4 w-4 shrink-0 mt-0.5 text-sag-navy" />
                  <p>{hasSignedAgreement ? "Service agreement signed. Proceeding to secure payment." : t("services.paymentSecure")}</p>
                </div>

                <Button onClick={handleCheckout} disabled={processing}
                  className="w-full h-12 bg-sag-navy hover:bg-sag-navy-800 text-white font-semibold" data-testid="pay-now-button">
                  {processing ? t("services.processing") : hasSignedAgreement ? (
                    <><CreditCard className="h-4 w-4 mr-2" /> {t("services.payNow")} ${calculateTotal().toFixed(2)}</>
                  ) : (
                    <><Shield className="h-4 w-4 mr-2" /> Sign Agreement & Pay ${calculateTotal().toFixed(2)}</>
                  )}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Hidden form for Fiserv IPG redirect */}
        <form ref={fiservFormRef} method="POST" style={{ display: "none" }} data-testid="fiserv-redirect-form" />
      </div>
    </DashboardLayout>
  );
}
